import { useEffect, useState } from "react";
import {
    Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { CurrencyDollar, Receipt, Trophy } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";
import StatCard from "@/admin/components/StatCard";

const PERIODS = [
    { id: "day", label: "Today" },
    { id: "week", label: "Last 7 days" },
    { id: "month", label: "Last 30 days" },
];

export default function Reports() {
    const [period, setPeriod] = useState("week");
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/admin/reports?period=${period}`, { headers: authHeaders() })
            .then((r) => setData(r.data))
            .catch(() => {});
    }, [period]);

    return (
        <div data-testid="admin-reports">
            <PageHeader
                kicker="Insights"
                title="Reports"
                action={
                    <div className="flex gap-2" data-testid="reports-period">
                        {PERIODS.map((p) => (
                            <button
                                key={p.id}
                                data-testid={`period-${p.id}`}
                                onClick={() => setPeriod(p.id)}
                                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border-2 transition ${
                                    period === p.id
                                        ? "bg-foreground text-background border-foreground"
                                        : "bg-white text-foreground/70 border-border hover:border-foreground"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard icon={CurrencyDollar} label="Revenue"
                    value={data ? `$${data.revenue.toFixed(2)}` : "$0.00"} testid="rep-revenue" />
                <StatCard icon={Receipt} label="Orders"
                    value={data?.orders ?? 0} testid="rep-orders" />
                <StatCard icon={Trophy} label="Avg ticket"
                    value={data ? `$${data.avg_ticket.toFixed(2)}` : "$0.00"} testid="rep-avg" />
            </div>

            <Card className="p-6 mt-6">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Revenue timeline
                </p>
                <h2 className="font-display font-black text-2xl mt-1 mb-4">
                    {PERIODS.find((p) => p.id === period)?.label}
                </h2>
                <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data?.timeline ?? []}>
                            <defs>
                                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#E87811" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#E87811" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Area type="monotone" dataKey="revenue" stroke="#E87811" fill="url(#rev)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Card className="p-6 mt-6">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Best sellers
                </p>
                <h2 className="font-display font-black text-2xl mt-1 mb-4">Top items</h2>
                {data?.top_items?.length ? (
                    <ol className="space-y-2" data-testid="top-items">
                        {data.top_items.map((it, i) => (
                            <li
                                key={it.id}
                                data-testid={`top-${it.id}`}
                                className="flex items-center justify-between gap-4 py-2 border-b last:border-0 border-border"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="grid place-items-center w-8 h-8 rounded-full bg-secondary text-secondary-foreground font-display font-black text-sm">
                                        {i + 1}
                                    </span>
                                    <span className="font-semibold truncate">{it.name}</span>
                                </div>
                                <div className="text-right">
                                    <p className="font-display font-black text-lg">{it.qty} sold</p>
                                    <p className="text-xs text-muted-foreground">${it.revenue.toFixed(2)}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="text-sm text-muted-foreground">No paid orders in this period.</p>
                )}
            </Card>
        </div>
    );
}
